import React, { useEffect, useState } from 'react';
import contributors from './contributors.json';
import artData from './art.json';

import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import "swiper/css";
import "swiper/css/navigation";
import Modal from './Modal';
import { useIsMobile } from './fmod/helpers';
import themes from './styles/themes.json';
import { useTheme } from './ThemeProvider';

type Contributor = typeof contributors.soundtomb;

const ArtPicker: React.FC = () => {

    const { theme, art, themeKey, artKey, setThemeKey, setArtKey } = useTheme();
    const [open, setOpen] = useState(false);
    const artistInfo = contributors[art.artist as keyof typeof contributors];
    const [imagesLoaded, setImagesLoaded] = useState(false);


    const mobile = useIsMobile(1000);

    const preloadImages = async () => {
        const promises = artData.map((art) => {
            return new Promise<void>((resolve) => {
                const img = new Image();
                img.src = art.thumbnailUrl;
                img.onload = () => resolve();
            });
        });
        await Promise.all(promises);
        setImagesLoaded(true);
    };

    useEffect(() => {
        preloadImages();
    }, []);

    const handleSelect = (idx: number) => {
        setArtKey(idx)
    };

    return (
        <>
            {!open && (
                <button
                    className="
                    rounded-[3px]
                    cursor-pointer
                    flex gap-1 items-center
                    transition-all
                    hover:animate-pulse
                    pl-5
                    group
                    "
                    title="Appearance"
                    onClick={() => setOpen(true)}
                >
                    <div className="bg-base0A w-3 h-3 rounded group-hover:bg-base09 transition-all" />
                    <div className="bg-base05 w-3 h-3 rounded group-hover:bg-base06 transition-all" />
                    <div className="bg-base0F w-3 h-3 rounded group-hover:bg-base0E transition-all" />
                </button>
            )}
            <Modal
                open={open}
                onClose={() => setOpen(false)}
            >
                <section className="flex flex-col items-center gap-3">
                    <h2>Select Artwork</h2>
                    <Swiper
                        modules={[Navigation]}
                        slidesPerView={mobile ? 'auto' : 5 }
                        observer={true}
                        observeParents={true}
                        navigation
                        className="px-10"
                        onSlideChange={(swiper) => {
                            if (mobile) {
                                handleSelect(swiper.realIndex);
                            }
                        }}
                        onSwiper={(swiper) => {
                            swiper.update();
                            if (mobile) {
                                swiper.slideTo(artKey);
                            }
                        }}
                        key={mobile ? 'mobile' : 'desktop'}
                    >
                        {artData.map((art, index) => (
                            <SwiperSlide key={index}>
                                <div className="flex justify-center">
                                    <button
                                        className="
                                        bg-transparent
                                        border-0
                                        text-inherit
                                        font-inherit
                                        p-[2px]
                                        rounded-lg
                                        flex flex-col
                                        self-center
                                        transition-colors
                                        duration-300 ease-in-out
                                        hover:bg-base0A
                                        hover:text-darkTint
                                        hover:font-bold
                                        "
                                        onClick={() => handleSelect(index)}
                                    >
                                        {imagesLoaded ? (
                                            <img
                                                className="rounded-lg"
                                                src={art.thumbnailUrl}
                                                width={150}
                                                height={150}
                                            />
                                        ) : (
                                                <div className="rounded-lg bg-base05" style={{
                                                    width: 150, height: 150
                                                }}>

                                                </div>
                                            )}
                                        
                                    </button>
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                    <div className="m-4" />
                    <label htmlFor="theme-select"><h2>Select Colorscheme</h2></label>
                    <select
                        id="theme-select"
                        value={themeKey}
                        onChange={e => {
                            if (e.target.value === 'auto') {
                                setThemeKey('auto');
                                return;
                            }

                            const index = Number.parseInt(e.target.value);
                            if (Number.isNaN(index)) {
                                throw new Error(`Invalid theme key: ${index}`);
                            }
                            setThemeKey(index);
                        }}
                        className="bg-base00 text-base04 border-b-1 border-base04 hover:text-base06 hover:border-base06 transition-[border] transition-[text] ease"
                    >
                        {
                            [
                                <option value="auto" key="auto">Match with art</option>
                            ].concat(
                                themes.map((theme, index) => (
                                    <option value={index} key={index}>
                                        {theme.name}
                                    </option>
                                ))
                            )
                        }
                    </select>
                    <div className="grid grid-cols-8 w-fit">
                        {
                            Object.entries(theme.palette).map(([key, value]) =>
                                <button
                                    key={key}
                                    className="w-8 h-8 rounded border m-1 transition-colors ease"
                                    style={{
                                        backgroundColor: value
                                    }}
                                >
                                </button>
                            )
                        }
                    </div>
                </section>
            </Modal>
        </>
    );
};

export default ArtPicker;
